// src/components/VeilleForm.jsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { createVeille } from '../utils/apiClient';

const FormContainer = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 600px;
  margin: 2rem auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: bold;
  color: #333;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 150px;
  font-size: 1rem;
  resize: vertical;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #3b78e7;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Alert = styled.div`
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  background-color: ${props => props.type === 'error' ? '#f8d7da' : '#d4edda'};
  color: ${props => props.type === 'error' ? '#721c24' : '#155724'};
`;

const VeilleForm = () => {
  const [themes, setThemes] = useState('');
  const [destinataire, setDestinataire] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!themes.trim()) {
      setAlert({ type: 'error', message: 'Veuillez saisir au moins un thème de veille.' });
      return;
    }
    
    if (!validateEmail(destinataire)) {
      setAlert({ type: 'error', message: 'Veuillez saisir une adresse email valide.' });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Créer la veille via l'API (PostgreSQL)
      await createVeille({ themes, destinataire });
      
      // Appeler le webhook https://wonder1709.app.n8n.cloud/webhook/d95d3104-246d-4c72-a3c7-0205f06faf93 de n8n pour lancer la veille directement avec un bodu json contenant l'email du destinataire et les thèmes
      // const webhookUrl = 'https://wonder1709.app.n8n.cloud/webhook/d95d3104-246d-4c72-a3c7-0205f06faf93';
      const webhookUrl = 'https://neobotiqc.app.n8n.cloud/webhook/app-veille';
      const webhookData = {
        "destinataire":destinataire,
        "themes":themes,
        "notloop":true
      };
      const webhookResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(webhookData)
      });

      if (!webhookResponse.ok) {
        throw new Error(`Erreur lors de l'appel du webhook: ${webhookResponse.statusText}`);
      }
          
      setAlert({ type: 'success', message: 'Veille créée avec succès!' });
      setThemes('');
      setDestinataire('');
    } catch (error) {
      console.error('Erreur lors de la création de la veille:', error);
      setAlert({ type: 'error', message: `Erreur: ${error.message}` });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <FormContainer>
      <h2>Créer une nouvelle veille</h2>
      
      {alert && (
        <Alert type={alert.type}>
          {alert.message}
        </Alert>
      )}
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="themes">Thèmes de veille</Label>
          <Textarea
            id="themes"
            value={themes}
            onChange={(e) => setThemes(e.target.value)}
            placeholder="Entrez les thèmes de veille"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="destinataire">Email du destinataire</Label>
          <Input
            id="destinataire"
            type="email"
            value={destinataire}
            onChange={(e) => setDestinataire(e.target.value)}
            placeholder="exemple@email.com"
            required
          />
        </FormGroup>
        
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Création en cours...' : 'Créer la veille'}
        </Button>
      </Form>
    </FormContainer>
  );
};

export default VeilleForm;